import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, AlertCircle, ArrowRight, LogIn } from 'lucide-react';
import type { OrganizationInfo } from '../lib/portal-loaders';
import { getErrorMessage } from '../lib/apiErrors';

interface MagicLinkVerifyProps {
  organizationInfo?: OrganizationInfo;
}

export const MagicLinkVerify: React.FC<MagicLinkVerifyProps> = () => {
  const { authenticateWithMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const mounted = useRef(true);
  
  // Determine URL pattern and construct URLs
  const isPathBased = window.location.pathname.startsWith('/org/');
  const getPortalUrl = useCallback((path: string) => {
    if (isPathBased) {
      // Extract org slug from current path
      const match = window.location.pathname.match(/^\/org\/([^/]+)/);
      if (match) {
        return `/org/${match[1]}/portal${path}`;
      }
    }
    return `/portal${path}`;
  }, [isPathBased]);

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const orgId = urlParams.get('org');
  const malformedMessage = !token
    ? 'Invalid magic link. The link appears to be malformed.'
    : !orgId
      ? 'Invalid magic link. Organization information is missing.'
      : null;

  // The link is only spent when the customer clicks "Sign in", never on page load. Corporate email security
  // scanners (e.g. Microsoft Defender Safe Links) open links in a real browser; if loading this page spent the
  // one-time token, a scan before the customer's click would lock them out. Scanners don't click the button.
  const [status, setStatus] = useState<'ready' | 'loading' | 'success' | 'error' | 'expired'>(malformedMessage ? 'error' : 'ready');
  const [message, setMessage] = useState(malformedMessage ?? 'Click below to finish signing in to your support portal.');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  // Set when verification failed for a server/connection reason rather than a bad link, so retrying can work
  const [canRetry, setCanRetry] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const showSuccessAndRedirect = () => {
    setStatus('success');
    setMessage('Successfully authenticated! Redirecting to your portal...');
    // Only a page of this portal: the link's ?redirect= comes from the URL, so anything else is ignored
    const requested = urlParams.get('redirect');
    const redirect = requested && requested.startsWith(getPortalUrl('/')) && !requested.startsWith('//') && !requested.includes('\\')
      ? requested
      : getPortalUrl('/tickets');
    setRedirectUrl(redirect);

    // Redirect after a brief delay to show success message
    setTimeout(() => {
      if (mounted.current) {
        navigate({ to: redirect });
      }
    }, 500);
  };

  const handleSignIn = async () => {
    if (!token || !orgId || status === 'loading') return;

    setStatus('loading');
    setMessage('Please wait while we verify your magic link...');
    setCanRetry(false);

    try {
      const authSuccess = await authenticateWithMagicLink(token, orgId);
      if (!mounted.current) return;

      if (authSuccess) {
        showSuccessAndRedirect();
      } else {
        setStatus('error');
        setMessage('This magic link is invalid, expired, or has already been used. Please request a new one.');
      }
    } catch (error) {
      if (!mounted.current) return;

      console.error('Magic link verification error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('already been used')) {
        if (isAuthenticated) {
          // This browser already signed in with the link (e.g. a second click) - carry on to the portal
          showSuccessAndRedirect();
        } else {
          setStatus('error');
          setMessage('This magic link has already been used. If you signed in with it on another device or browser, you can continue there. Otherwise, please request a new one.');
        }
      } else if (errorMessage.includes('expired')) {
        setStatus('expired');
        setMessage('This magic link has expired. Magic links are valid for 30 minutes.');
      } else if (errorMessage.includes('invalid')) {
        setStatus('error');
        setMessage('This magic link is invalid. Please request a new one.');
      } else {
        // Offer a retry only for a connection/server problem (ApiService.handleError's transient messages), where
        // the link itself may still be good; anything else is a problem with the link that retrying can't fix
        setStatus('error');
        setCanRetry(/unable to reach the server|too many requests|unexpected error/i.test(errorMessage));
        setMessage(getErrorMessage(error, 'An error occurred while verifying your magic link. Please try again.'));
      }
    }
  };

  const handleRequestNewLink = () => {
    navigate({ to: getPortalUrl('/login') });
  };

  const handleContinue = () => {
    if (redirectUrl) {
      navigate({ to: redirectUrl });
    } else {
      navigate({ to: getPortalUrl('/tickets') });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center">
            {status === 'ready' && (
              <div className="bg-blue-100 p-3 rounded-full">
                <LogIn className="h-6 w-6 text-blue-600" />
              </div>
            )}
            {status === 'loading' && (
              <div className="bg-blue-100 p-3 rounded-full">
                <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            )}
            {(status === 'error' || status === 'expired') && (
              <div className="bg-red-100 p-3 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            )}
          </div>
          <CardTitle className="text-xl">
            {status === 'ready' && 'Sign In to Your Portal'}
            {status === 'loading' && 'Verifying Magic Link...'}
            {status === 'success' && 'Welcome Back!'}
            {status === 'error' && 'Verification Failed'}
            {status === 'expired' && 'Link Expired'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className={
            status === 'success' ? 'border-green-200 bg-green-50' : 
            status === 'loading' || status === 'ready' ? 'border-blue-200 bg-blue-50' :
            'border-red-200 bg-red-50'
          }>
{status === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : status === 'error' || status === 'expired' ? (
              <AlertCircle className="h-4 w-4 text-red-600" />
            ) : null}
            <AlertDescription className={
              status === 'success' ? 'text-green-800' : 
              status === 'loading' || status === 'ready' ? 'text-blue-800' :
              'text-red-800'
            }>
              {message}
            </AlertDescription>
          </Alert>

          {status === 'ready' && (
            <Button
              onClick={handleSignIn}
              className="w-full"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign In
            </Button>
          )}

          {status === 'loading' && (
            <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
              <p>Please wait while we verify your magic link...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                <p>You're now logged in and can access your support portal.</p>
              </div>
              <Button 
                onClick={handleContinue}
                className="w-full"
              >
                Continue to Portal
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {(status === 'error' || status === 'expired') && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                {status === 'expired' ? (
                  <p>For security reasons, magic links expire after 30 minutes. Please request a new one.</p>
                ) : canRetry ? (
                  <p>Your link may still be valid. You can try again, or request a new one.</p>
                ) : (
                  <p>Something went wrong with your magic link. Please try requesting a new one.</p>
                )}
              </div>
              {canRetry && (
                <Button
                  onClick={handleSignIn}
                  className="w-full"
                >
                  Try Again
                </Button>
              )}
              <Button 
                onClick={handleRequestNewLink}
                className="w-full"
                variant="outline"
              >
                Request New Magic Link
              </Button>
            </div>
          )}

          {status !== 'loading' && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              <p>Need help? Contact support for assistance.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLinkVerify;